<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Mail } from 'lucide-vue-next'
import { useRoute, useRouter } from 'vue-router'
import AuthShell from '@/components/AuthShell.vue'
import { ApiError } from '@/services/api'
import { authSession, formatAuthError, safeReturnPath, type WechatPairingOutcome } from '@/services/auth'

type LoginViewStatus =
  | 'loading'
  | 'oauthReady'
  | 'completing'
  | 'pairing'
  | 'emailRequired'
  | 'error'

const route = useRoute()
const router = useRouter()
const status = ref<LoginViewStatus>('loading')
const qrUrl = ref('')
const remember = ref(false)
const errorMessage = ref('')
const pollNotice = ref('')
const pairingSeconds = ref(0)
const email = ref('')
const emailCode = ref('')
const emailSending = ref(false)
const emailBinding = ref(false)
const emailFeedback = ref('')
const resendSeconds = ref(0)
const returnPath = computed(() => safeReturnPath(route.query.redirect))
const pairing = computed(() => authSession.wechatPairing.value)
const initialPhase = computed(() => ['loading', 'oauthReady', 'completing', 'error'].includes(status.value))
const pairingTime = computed(() => {
  const minutes = Math.floor(pairingSeconds.value / 60)
  const seconds = String(pairingSeconds.value % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})
const heading = computed(() => {
  if (status.value === 'pairing') {
    return {
      title: '再确认一次账号',
      copy: '扫描小程序码，在小程序里核对账号信息后确认。',
    }
  }
  if (status.value === 'emailRequired') {
    return {
      title: '绑定邮箱后登录',
      copy: '这个账号需要用邮箱验证码确认归属，完成后即可登录。',
    }
  }
  return {
    title: '微信扫码登录',
    copy: '打开手机微信，扫描下方二维码完成授权。',
  }
})

let pollTimer: number | undefined
let clockTimer: number | undefined
let resendAvailableAt = 0
let polling = false
let disposed = false
let flowGeneration = 0

const stopPolling = () => {
  if (pollTimer !== undefined) window.clearTimeout(pollTimer)
  pollTimer = undefined
}

const stopClock = () => {
  if (clockTimer !== undefined) window.clearInterval(clockTimer)
  clockTimer = undefined
}

const resetEmailForm = () => {
  email.value = ''
  emailCode.value = ''
  emailFeedback.value = ''
  emailSending.value = false
  emailBinding.value = false
  resendAvailableAt = 0
  resendSeconds.value = 0
}

const failPairing = (message: string) => {
  flowGeneration += 1
  stopPolling()
  stopClock()
  authSession.resetWechatLogin()
  status.value = 'error'
  errorMessage.value = message
  pollNotice.value = ''
}

const updateClock = () => {
  const expiresAt = pairing.value ? Date.parse(pairing.value.expiresAt) : Number.NaN
  pairingSeconds.value = Number.isFinite(expiresAt)
    ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
    : 0
  resendSeconds.value = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000))
  if (pairingSeconds.value === 0 && (status.value === 'pairing' || status.value === 'emailRequired')) {
    failPairing('本次账号确认已过期，请重新获取二维码。')
  }
}

const startClock = () => {
  stopClock()
  updateClock()
  if (status.value === 'pairing' || status.value === 'emailRequired') {
    clockTimer = window.setInterval(updateClock, 1000)
  }
}

const handlePairingOutcome = async (outcome: WechatPairingOutcome) => {
  if (outcome.status === 'LOGGED_IN' && outcome.returnPath) {
    stopPolling()
    await router.replace(outcome.returnPath)
    return
  }
  if (outcome.status === 'EMAIL_REQUIRED') {
    stopPolling()
    status.value = 'emailRequired'
    pollNotice.value = ''
    return
  }
  if (outcome.status === 'CANCELLED') {
    failPairing('你已在小程序中取消本次登录，请重新获取二维码。')
    return
  }
  if (outcome.status === 'EXPIRED') {
    failPairing('本次账号确认已过期，请重新获取二维码。')
  }
}

const retryablePollError = (error: unknown) => error instanceof ApiError
  && (error.code === 'NETWORK' || (typeof error.code === 'number' && error.code >= 500))

const pollPairing = async () => {
  if (disposed || status.value !== 'pairing' || polling) return
  const generation = flowGeneration
  polling = true
  try {
    const outcome = await authSession.pollWechatPairing()
    if (disposed || generation !== flowGeneration) return
    pollNotice.value = ''
    await handlePairingOutcome(outcome)
  } catch (error) {
    if (disposed || generation !== flowGeneration || status.value !== 'pairing') return
    if (retryablePollError(error)) {
      pollNotice.value = '网络暂时不稳定，正在继续等待确认。'
    } else {
      failPairing(formatAuthError(error, '无法读取账号确认状态，请重新扫码。'))
    }
  } finally {
    polling = false
    if (!disposed && generation === flowGeneration && status.value === 'pairing') {
      pollTimer = window.setTimeout(pollPairing, pollNotice.value ? 2500 : 1500)
    }
  }
}

const startPairing = () => {
  if (!pairing.value) {
    failPairing('微信登录确认信息缺失，请重新扫码。')
    return
  }
  status.value = pairing.value.status === 'EMAIL_REQUIRED' ? 'emailRequired' : 'pairing'
  errorMessage.value = ''
  pollNotice.value = ''
  startClock()
  if (status.value === 'pairing') pollPairing()
}

const useEmailInstead = () => {
  stopPolling()
  status.value = 'emailRequired'
  pollNotice.value = ''
}

const loadWechatQr = async () => {
  const generation = ++flowGeneration
  stopPolling()
  authSession.resetWechatLogin()
  resetEmailForm()
  status.value = 'loading'
  qrUrl.value = ''
  errorMessage.value = ''
  try {
    const callback = new URL(
      router.resolve({ name: 'login', query: { wechat: 'callback' } }).href,
      window.location.origin,
    ).toString()
    const nextQrUrl = await authSession.beginWechatLogin(callback, returnPath.value, remember.value)
    if (generation !== flowGeneration) return
    qrUrl.value = nextQrUrl
    status.value = 'oauthReady'
  } catch (error) {
    if (generation !== flowGeneration) return
    status.value = 'error'
    errorMessage.value = formatAuthError(error, '二维码加载失败，请稍后重试')
  }
}

const sendEmailCode = async () => {
  const normalizedEmail = email.value.trim()
  if (!normalizedEmail || !normalizedEmail.includes('@')) {
    emailFeedback.value = '请输入正确的邮箱地址。'
    return
  }
  if (emailSending.value || resendSeconds.value > 0) return
  emailSending.value = true
  emailFeedback.value = ''
  try {
    await authSession.sendWechatPairingEmailCode(normalizedEmail)
    resendAvailableAt = Date.now() + 60_000
    updateClock()
    emailFeedback.value = '验证码已发送，请检查邮箱。'
  } catch (error) {
    emailFeedback.value = formatAuthError(error, '验证码发送失败，请稍后重试。')
  } finally {
    emailSending.value = false
  }
}

const bindEmail = async () => {
  if (emailBinding.value) return
  const normalizedEmail = email.value.trim()
  const normalizedCode = emailCode.value.trim()
  if (!normalizedEmail || !normalizedEmail.includes('@')) {
    emailFeedback.value = '请输入正确的邮箱地址。'
    return
  }
  if (!/^\d{6}$/.test(normalizedCode)) {
    emailFeedback.value = '请输入 6 位邮箱验证码。'
    return
  }
  emailBinding.value = true
  emailFeedback.value = ''
  try {
    const outcome = await authSession.bindWechatPairingEmail(normalizedEmail, normalizedCode)
    await handlePairingOutcome(outcome)
  } catch (error) {
    emailFeedback.value = formatAuthError(error, '邮箱确认失败，请检查验证码后重试。')
  } finally {
    emailBinding.value = false
  }
}

const restart = () => {
  flowGeneration += 1
  stopPolling()
  authSession.resetWechatLogin()
  stopClock()
  resetEmailForm()
  status.value = 'loading'
  qrUrl.value = ''
  errorMessage.value = ''
  pollNotice.value = ''
  pairingSeconds.value = 0
  void loadWechatQr()
}

const queryValue = (value: unknown) => typeof value === 'string' ? value : ''

onMounted(async () => {
  if (pairing.value) {
    startPairing()
    return
  }
  const providerError = queryValue(route.query.error_description) || queryValue(route.query.error)
  const code = queryValue(route.query.code)
  const state = queryValue(route.query.state)
  if (!providerError && !code && !state) {
    await loadWechatQr()
    return
  }
  if (providerError || !code || !state) {
    status.value = 'error'
    errorMessage.value = providerError || '微信没有返回完整的登录信息，请重新扫码'
    return
  }
  status.value = 'completing'
  try {
    const completion = await authSession.finishWechatLogin(code, state)
    if (completion.nextAction === 'LOGGED_IN') {
      await router.replace(completion.returnPath)
    } else {
      startPairing()
    }
  } catch (error) {
    status.value = 'error'
    errorMessage.value = formatAuthError(error, '微信登录未完成，请重新扫码')
  }
})

onBeforeUnmount(() => {
  disposed = true
  flowGeneration += 1
  stopPolling()
  stopClock()
  authSession.resetWechatLogin()
})
</script>

<template>
  <AuthShell>
    <div class="wechat-login-view">
      <header class="wechat-login-heading">
        <h1>{{ heading.title }}</h1>
        <p>{{ heading.copy }}</p>
      </header>

      <ol v-if="status === 'pairing' || status === 'emailRequired'" class="wechat-login-steps" aria-label="微信登录进度">
        <li class="done"><span>1</span><strong>微信授权</strong></li>
        <li class="active"><span>2</span><strong>{{ status === 'pairing' ? '小程序确认' : '邮箱确认' }}</strong></li>
      </ol>

      <section
        v-if="initialPhase"
        class="wechat-scan-area"
        aria-label="微信登录二维码"
        :aria-busy="status === 'loading' || status === 'completing'"
      >
        <iframe
          v-if="status === 'oauthReady' && qrUrl"
          class="wechat-qr-frame"
          :src="qrUrl"
          title="微信登录二维码"
          referrerpolicy="no-referrer"
          scrolling="no"
        ></iframe>
        <div v-else class="wechat-scan-placeholder">
          <span v-if="status === 'loading' || status === 'completing'" class="auth-spinner" aria-hidden="true"></span>
          <strong>{{ status === 'loading' ? '正在加载微信登录' : status === 'completing' ? '正在完成微信授权' : '微信登录暂时不可用' }}</strong>
          <p>{{ status === 'completing' ? '正在安全匹配已有账号，请稍候。' : status === 'error' ? errorMessage : '请稍候。' }}</p>
          <button v-if="status === 'error'" class="scan-load-action" type="button" @click="restart">
            重新加载
          </button>
        </div>
      </section>

      <section v-else-if="status === 'pairing' && pairing" class="wechat-pairing-panel" aria-live="polite">
        <div class="mini-code-frame">
          <img :src="pairing.qrCodeDataUrl" alt="Nova 小程序账号确认码" />
        </div>
        <strong>请再次用微信扫码</strong>
        <p>小程序会显示当前账号的昵称、姓名和脱敏手机号。核对无误后，再点击确认登录。</p>
        <div class="pairing-expiry">确认码将在 {{ pairingTime }} 后失效</div>
        <p v-if="pollNotice" class="pairing-notice">{{ pollNotice }}</p>
        <button class="pairing-email-choice" type="button" @click="useEmailInstead">没有小程序账号，使用邮箱</button>
        <button class="pairing-restart" type="button" @click="restart">换一个微信账号</button>
      </section>

      <section v-else class="wechat-email-panel">
        <Mail :size="34" :stroke-width="1.5" aria-hidden="true" />
        <p>输入你要绑定的邮箱。邮箱已有 Nova 账号时会登录该账号；没有账号时会创建新账号。</p>
        <form class="wechat-email-form" @submit.prevent="bindEmail">
          <label>
            <span>邮箱</span>
            <input v-model="email" type="email" maxlength="254" autocomplete="email" placeholder="name@example.com" :disabled="emailBinding" />
          </label>
          <label>
            <span>邮箱验证码</span>
            <div class="email-code-row">
              <input v-model="emailCode" type="text" maxlength="6" inputmode="numeric" autocomplete="one-time-code" placeholder="6 位验证码" :disabled="emailBinding" />
              <button type="button" :disabled="emailSending || emailBinding || resendSeconds > 0" @click="sendEmailCode">
                {{ emailSending ? '发送中' : resendSeconds > 0 ? `${resendSeconds} 秒后重发` : '发送验证码' }}
              </button>
            </div>
          </label>
          <button class="email-bind-action" type="submit" :disabled="emailBinding">
            {{ emailBinding ? '正在确认' : '确认邮箱并登录' }}
          </button>
        </form>
        <p v-if="emailFeedback" class="email-feedback" aria-live="polite">{{ emailFeedback }}</p>
        <div class="pairing-expiry">本次确认将在 {{ pairingTime }} 后失效</div>
        <button class="pairing-restart" type="button" @click="restart">重新扫码</button>
      </section>

      <template v-if="initialPhase">
        <div class="wechat-login-options">
          <label class="auth-check"><input v-model="remember" type="checkbox" :disabled="status === 'loading' || status === 'completing'" @change="loadWechatQr()" />保持登录</label>
        </div>
        <p class="auth-consent-note">登录即表示你已阅读并同意 <RouterLink to="/legal/agreement">用户协议</RouterLink> 和 <RouterLink to="/legal/privacy">隐私政策</RouterLink></p>
      </template>

      <p v-if="errorMessage && status !== 'error'" class="auth-feedback error" role="alert">{{ errorMessage }}</p>
      <p v-if="status === 'pairing'" class="auth-footnote">第一次扫码完成微信授权；第二次扫码只用于确认你要登录的小程序账号。</p>
      <p v-else-if="status === 'emailRequired'" class="auth-footnote">新账号需要通过邮箱验证码确认归属，避免把微信绑定到错误账号。</p>
      <p v-else class="auth-footnote">已有小程序账号会优先安全匹配；无法确认归属时，才会进入账号确认。</p>
    </div>
  </AuthShell>
</template>
